'use strict';
import { fh_box, fh_cross, fh_polygon, fh_scene, fh_solid } from '@acenv/fh-3d-viewer';
import { cn_camera } from '../svg/cn_camera';
import { CODES_BIM_OUVRAGES, CODES_BIM_PARAMETRES_OUVRAGES } from '../utils/cn_bbp_constants';
import { cn_bbp_geometry } from '../utils/cn_bbp_geometry';
import { BbpHelper } from '../utils/cn_bbp_helper';
import { logger } from '../utils/cn_logger';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Space segment class : matches a wall and a space. Bears facing
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//**** Internal : measure points
//***********************************************************************************
import {
    cn_add, cn_box, cn_cart,
    cn_clone,
    cn_color_hexa_to_rgb,
    cn_dist,
    cn_dot,
    cn_intersect_line,
    cn_mul,
    cn_normal,
    cn_normalize, cn_polar,
    cn_size,
    cn_sub,
    cn_translate_polygon,
    cnx_add,
    cnx_clone,
    cnx_cross,
    cnx_dot,
    cnx_manhattan_distance,
    cnx_mul,
    cnx_normalize,
    cnx_sub
} from '../utils/cn_utilities';
import { cn_image_dir } from '../utils/image_dir';
import { cn_3d_building } from './cn_3d_building';
import { cn_building } from './cn_building';
import { cn_element } from './cn_element';
import { cn_space } from './cn_space';
import { cn_storey } from './cn_storey';
import { cn_wall } from './cn_wall';
import { cn_wall_type } from './cn_wall_type';
import { cn_balcony_type } from './cn_balcony_type';
import { cn_fence_type } from './cn_fence_type';
import { cn_facing_trimming } from './cn_facing_trimming';
import { cn_molding_type } from './cn_molding_type';
import { cn_plugin_option } from '../utils/cn_plugin_option';

export class cn_space_segment extends cn_element {
    /**
     *
     * @param {cn_space} space
     */
    constructor(space) {
        super(space);
        this.class_name = 'cn_space_segment';
        this.space = space;

        //*** Model data */
        this.points = [[0, 0], [0, 0,]];
        this.facing = null;
        this.substrate = null;
        this.moldings = [];
        this.molding_facings = [];
        /** @type{cn_wall} */
        this.wall = null;
        this.wall_side = 0;
        this.wall_positions = [0, 0]; //*** Abissa of each point on the bearing wall, is wall side is 0 or 1. */

        //*** Volatile data */
        this.direction = [0, 0];
        this.normal = [0, 0];
        this.size = 0;
        this._bounding_box = new cn_box();
        this.numerotation = "";

        //*** Data for other facings */
        this.upper_facings = [];
        this.upper_substrates = [];
        this.upper_heights = [];
    }

    /**
     * Serialize
     */
    serialize() {
        var json = this.serialize_element();
        json.points = this.points;
        if (this.facing)
            json.facing = this.facing.ID
        if (this.substrate)
            json.substrate = this.substrate.ID
        if (this.wall)
            json.wall = this.wall.ID;
        json.moldings = this.moldings.map(m => m.ID);
        json.molding_facings = this.molding_facings.map(mf => (mf) ? mf.ID : "");
        json.wall_side = this.wall_side;
        json.numerotation = this.numerotation;

        json.upper_facings = this.upper_facings.map(f => f.ID);
        json.upper_substrates = this.upper_substrates.map(f => f.ID);
        json.upper_heights = this.upper_heights;

        return json;
    }

    compute_facing_count() {
        if (this.facing) this.facing.count++;
        this.upper_facings.forEach(f => f.count++);
        this.molding_facings.forEach(f => f.count++);
    }

    /**
     * Unserialize
     * @param {cn_space} space
     */
    static unserialize(json, space) {
        const space_segment = new cn_space_segment(space);
        space_segment.unserialize_element(json);
        if (typeof (json.points) == 'object')
            space_segment.points = json.points;
        if (typeof (json.facing) == 'string')
            space_segment.facing = space.scene.building.facing_types.find(ft => ft.ID === json.facing) || null;
        if (typeof (json.substrate) == 'string')
            space_segment.substrate = space.scene.building.get_mmm_compositions().find(ft => ft.ID === json.substrate) || null;
        if (typeof (json.wall) == 'string')
            space_segment.wall = space.scene.walls.find(w => w.ID == json.wall);
        if (space_segment.wall && !space_segment.wall.space_segments.includes(space_segment))
            space_segment.wall.space_segments.push(space_segment);
        if (typeof (json.wall_side) == 'number')
            space_segment.wall_side = json.wall_side;
        if (json.moldings) {
            let all_moldings = space.scene.building.get_molding_types();
            json.moldings.forEach((json_molding, i) => {
                let molding = all_moldings.find(mt => mt.ID === json_molding);
                if (molding) {
                    space_segment.moldings.push(molding);
                    let facing = null;
                    if (json.molding_facings && json.molding_facings[i] && json.molding_facings[i] != "")
                        facing = space.scene.building.facing_types.find(ft => ft.ID === json.molding_facings[i]);
                    space_segment.molding_facings.push(facing);
                }
            });
        }
        if (json.upper_heights) {
            space_segment.upper_heights = json.upper_heights;
            space_segment.upper_facings = json.upper_facings.map(fid => space.scene.building.facing_types.find(ft => ft.ID === fid) || null);
            if (json.upper_substrates)
                space_segment.upper_substrates = json.upper_substrates.map(fid => space.scene.building.get_mmm_compositions().find(ft => ft.ID === fid) || null);
            else
                space_segment.upper_substrates = json.upper_heights.map(v => null);
        }

        space_segment.update();
        return space_segment;
    }

    /**
     * Retruns a readable ID
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {string}
     */
    build_readable_id(storey) {
        let name = this.space.build_readable_id(storey) + "M";
        if (cn_plugin_option.numerotation)
            name += this.numerotation;
        else {
            let loop = this.space.space_segments.findIndex(l => l.includes(this));
            if (loop < 0)
                name += this.ID;
            else {
                if (loop > 0)
                    name += "." + (loop);
                name += (this.space.space_segments[loop].indexOf(this) + 1);
            }
        }
        return name;
    }

    /**
     * Update
     */
    update_wall_positions() {
        if (this.wall && this.wall_side < 2) {
            this.wall_positions[0] = this.wall.compute_abcissa(this.points[0]);
            this.wall_positions[1] = this.wall.compute_abcissa(this.points[1]);
        }
    }
    /**
     * Update
     */
    update() {
        this.direction = cn_sub(this.points[1], this.points[0]);
        this.size = cn_normalize(this.direction);
        this.normal = cn_normal(this.direction);
        this._bounding_box = new cn_box();
        this._bounding_box.enlarge_point(this.points[0]);
        this._bounding_box.enlarge_point(this.points[1]);
    }

    /**
     * Bounding box calculation
     * @returns {cn_box}
     */
    get_bounding_box() {
        return this._bounding_box;
    }

    /**
     * True if the space line contains point with given precisionb
     * @param {number[]} p
     * @param {number} tolerance
     */
    contains(p, tolerance) {
        if (!this._bounding_box.contains_point(p, tolerance)) return false;

        const op = cn_sub(p, this.points[0]);
        if (Math.abs(cn_dot(this.normal, op)) > tolerance) return false;
        const x = cn_dot(op, this.direction);
        return (x >= -tolerance && x <= this.size + tolerance);
    }

    draw(camera, add_classes = [], raw_style = '') {
        let svg = "";

        const p0 = camera.world_to_screen(this.points[0]);
        const p1 = camera.world_to_screen(this.points[1]);
        let normal = cn_normal(cn_sub(p1, p0))
        cn_normalize(normal)
        normal = cn_mul(normal, 3);
        const pA = cn_add(p0, normal);
        const pB = cn_sub(p0, normal);
        const pD = cn_add(p1, normal);
        const pC = cn_sub(p1, normal);
        if (raw_style == '' && add_classes.includes("mouseover"))
            raw_style = `style="stroke: none; fill: rgb(var(--color-mouseover))"`;
        svg = `<path ${raw_style} d="M ${pA[0]} ${pA[1]} L ${pB[0]} ${pB[1]} ${pC[0]} ${pC[1]} ${pD[0]} ${pD[1]} Z" />`;

        return svg;
    }

    draw_substrate(camera, extra, fill = "") {
        let html = "";
        if (!this.substrate) {
            if (extra.includes("mouseover")) {
                let p1 = camera.world_to_screen(this.points[0]);
                let p2 = camera.world_to_screen(this.points[1]);
                html += `<line x1="${p1[0]}" y1="${p1[1]}" x2="${p2[0]}" y2="${p2[1]}" style="stroke:rgb(255,136,0);stroke-width:3px;" />`;
            }
        }
        else {
            let th = this.substrate.thickness;
            if (th > this.wall.wall_type.thickness)
                th = this.wall.wall_type.thickness;

            let normals = [this.normal, this.normal];
            [0, 1].forEach(b => {
                let other = (b == 0) ? this.get_previous() : this.get_next();
                if (!other) return;
                if (other.wall != this.wall) {
                    if (!other.wall || !this.wall) return;
                    let vertices = this.wall.vertices;
                    if (!vertices.includes(other.wall.vertices[0]) && !vertices.includes(other.wall.vertices[1]))
                        return;
                }
                normals[b] = cn_add(this.normal, other.normal);
                cn_normalize(normals[b]);
                normals[b] = cn_mul(normals[b], 1 / cn_dot(normals[b], this.normal));
            })
            let path = [this.points[0], this.points[1], cn_add(this.points[1], cn_mul(normals[1], th)), cn_add(this.points[0], cn_mul(normals[0], th))];

            html += this.substrate.draw_shape(camera, path, this.points[0], this.normal);

            if (extra.includes("mouseover")) {
                html += camera.draw_path(path, "", true, `style="stroke:rgb(255,136,0);stroke-width:3px;fill:rgba(255,136,0,0.3)"`);
            }
        }

        html += camera.draw_textured_line(this.ID, this.points[0], this.points[1]);
        return html;
    }

    /**
     * Returns true if this is a facade wall.
     * If side is not defined (negative value), true is answered if one side at least is a facade side.
     * @param {cn_storey} storey
     * @returns {boolean}
     */
    is_facade(storey = null) {
        if (!this.wall) return false;
        if (this.wall.dormer) return (this.wall_side != 0);
        if (this.wall.wall_type.free) return false;

        if (this.space.scene.storey.exterior) return true;
        if (this.space.is_indoor(storey)) return false;
        if (this.wall.balcony) {
            return this.space.outside;
        } else {
            if (!this.space.is_roof(storey)) return true;
        }
        return false;
    }

    /**
     * Returns true if the given side of the wall is outdoor.
     * @param {cn_storey} storey
     * @returns {boolean}
     */
    is_outdoor(storey = null) {
        if (this.space.scene.storey.exterior) return true;
        if (this.wall && this.wall.is_wall_dormer()) return this.wall_side == 1;
        return !this.space.is_indoor(storey);
    }

    create_id_for_bbp(storey, facing_index) {
        const space_segment_index = this.wall.space_segments.filter(ss => ss.wall_side == this.wall_side).indexOf(this);
        return cn_building.create_unique_id(storey, this.wall, this.wall_side, space_segment_index, facing_index, 'facing')
    }



    /**
     * Builds bbp objects that match facings
     * @param {cn_storey} storey
     * @param {boolean} cnmap_pointers
     * @param {BbpHelper} bbpHelper
     * @returns
     */
    build_bbp_facings(storey, cnmap_pointers = false, bbpHelper = null) {
        if (!this.wall || !this.wall.has_facings()) return [];

        //*** Build wall facings */
        var facings = this.build_facing_polygons(storey);

        const bbp_objects = [];
        facings.forEach(facing_polygon => {
            const area = facing_polygon.get_area();
            if (area > 0.01) {
                //*** build facing */
                var bbp_facing = {};
                bbp_objects.push(bbp_facing);
                bbp_facing.ID = facing_polygon['bbp_id'];
                if (this.is_outdoor()) {
                    bbp_facing.Name = 'Revêtement de façade';
                    bbp_facing.Code_BIM = CODES_BIM_OUVRAGES.facing_facade;
                } else {
                    bbp_facing.Name = 'Revêtement de mur';
                    bbp_facing.Code_BIM = CODES_BIM_OUVRAGES.facing_wall;
                    if (this.space) bbp_facing.SPACE = this.space.create_id_for_bbp(storey);
                }
                if (bbpHelper) {
                    Object.assign(bbp_facing, bbpHelper.getWallFacingParameters(this, this.wall_side));
                }
                bbp_facing.storey = (this.wall && this.wall.dormer) ? storey.storey_index + 1 : storey.storey_index;
                bbp_facing[CODES_BIM_PARAMETRES_OUVRAGES.facing_surface] = area;
                bbp_facing[CODES_BIM_PARAMETRES_OUVRAGES.wall_facing] = cn_building.create_unique_id(storey, this);
                if (cnmap_pointers) {
                    bbp_facing.cnmap_storey = storey;
                    bbp_facing.cnmap_space_segment = this;
                    if (facing_polygon['facing_trimming'])
                        bbp_facing.cnmap_element = facing_polygon['facing_trimming'];
                    else {
                        bbp_facing.cnmap_element = this;
                        bbp_facing.cnmap_trimmings = [];
                        facings.forEach(f => {
                            if (f['facing_trimming']) bbp_facing.cnmap_trimmings.push(f)
                        });
                    }
                }

                const geometry = cn_bbp_geometry.from_polygon(facing_polygon, storey.altitude);
                const facing = facing_polygon['facing'];
                if (facing) {
                    geometry.texture = cn_image_dir() + 'texture_' + facing.texture + '.jpg';
                    geometry.color = [...cn_color_hexa_to_rgb(facing.color || '#FFFFFF'), 1];
                }

                bbp_facing.geometries = [geometry];

                if (storey.exterior) {
                    bbp_facing.storey = storey.building.storeys[storey.building.storey_0_index].storey_index;
                    bbp_facing.topography = '';
                }
            }
        });

        return bbp_objects;
    }

    /**
     * Updates facings textures
     * @param {cn_3d_building} building_3d
     * @param {cn_storey} storey
     */
    update_3d_facing_texture(building_3d, storey) {
        const objects = building_3d.get_3d_objects(this).filter(obj => obj.cnmap_storey == storey && (obj.json_object.Code_BIM == CODES_BIM_OUVRAGES.facing_wall || obj.json_object.Code_BIM == CODES_BIM_OUVRAGES.facing_facade));
        const target_facing = {
            texture: this.facing ? cn_image_dir() + 'texture_' + this.facing.texture + '.jpg' : '',
            color: this.facing && this.facing.color ? this.facing.color : '#FFFFFF'
        }
        objects.forEach(obj => {
            const target_color = [...cn_color_hexa_to_rgb(target_facing.color || '#FFFFFF'), 1];
            fh_scene.update_mesh_color(obj._meshes[0], target_color, target_facing.texture);
            obj._meshes[0]._original_material.needsUpdate = true;
        });
    }

    /**
     * Tries to concile moldings
     * @param {cn_space_segment[]} space_segments
     * @returns
     */
    concile_moldings(space_segments) {
        if (space_segments.length == 0) return;
        if (space_segments.length == 1) {
            this.moldings = space_segments[0].moldings.concat([]);
            this.molding_facings = space_segments[0].molding_facings.concat([]);
            return;
        }
        space_segments[0].moldings.forEach((molding, i) => {
            if (space_segments.some(ss => !ss.moldings.includes(molding))) return;
            this.moldings.push(molding);
            let facing = space_segments[0].molding_facings[i];
            if (space_segments.some(ss => facing != ss.molding_facings[ss.moldings.indexOf(molding)]))
                facing = null;
            this.molding_facings.push(facing);
        });
    }

    /**
     * Builds all facing polygons
     * @param {cn_storey} storey
     * @param {boolean} apply_openings
     * @param {function} facing_filter - If defined, a boolean function that returns true if facing is concerned.
     * @returns {fh_polygon[]}
     */
    build_facing_polygons(storey, apply_openings = true, facing_filter = null) {
        if (apply_openings && !facing_filter) {
            // @ts-ignore
            return this.get_lazy_data(() => {
                return this._build_facing_polygons(storey);
            }, "build_facing_polygons", storey)
        } else {
            return this._build_facing_polygons(storey, apply_openings, facing_filter);
        }
    }

    /**
     * Returns area
     * @param {cn_storey} storey
     * @param {boolean} apply_openings
     * @returns {number}
     */
    get_area(storey, apply_openings = true) {
        let area = 0;
        this.build_facing_polygons(storey, apply_openings).forEach(pg => area += pg.get_area());
        return area;
    }

    _dummy_space_segment(wall, z0, z1) {
        let ss = new cn_space_segment(this.space);
        ss.points = wall.get_side_segment(this.wall_side).map(v => cn_clone(v));
        ss.wall = wall;
        ss.wall_side = this.wall_side;
        ss.points[0].push(z0);
        ss.points[0].push(z1);
        ss.points[1].push(z0);
        ss.points[1].push(z1);
        return ss;
    }

    /**
     *
     * @returns {cn_space_segment[]}
     */
    _get_dummy_segments() {
        if (this.wall.dormer) return [this];

        let neighbours = [null, null];
        for (var nv = 0; nv < 2; nv++) {
            for (var x = 1; ; x++) {
                let ss = this.get_other((nv == 0) ? -x : x);
                if (ss.wall) {
                    neighbours[nv] = ss;
                    break;
                }
            }
        }
        if (!this.wall.has_verticality() && !neighbours[0].wall.has_verticality() && !neighbours[1].wall.has_verticality()) return [this];

        let dummy_segments = this.wall.get_dummy_walls().map(wall => {
            let z0 = (typeof (wall["_z0"]) == 'number') ? wall["_z0"] : -1000;
            let z1 = (typeof (wall["_z1"]) == 'number') ? wall["_z1"] : 1000;
            return this._dummy_space_segment(wall, z0, z1);
        });

        if (this.wall_side < 2) {
            for (var niter = 0; niter < 2; niter++) {
                let dummy_walls = neighbours[niter].wall.get_dummy_walls();
                if (dummy_walls.length == 1) {
                    dummy_walls[0]["_z0"] = -1000;
                    dummy_walls[0]["_z1"] = 1000;
                }
                let other_side = neighbours[niter].wall_side;

                let nz0 = 0;
                let nz1 = 0;
                while (nz0 < dummy_segments.length && nz1 < dummy_walls.length) {
                    let ss = dummy_segments[nz0];
                    let z1 = ss.points[0][3];
                    let zz1 = dummy_walls[nz1]["_z1"];

                    let d0 = cn_sub(ss.points[niter], ss.points[1 - niter]);
                    let p0 = ss.points[1 - niter];
                    let lc = cn_normalize(d0);
                    let other_segment = dummy_walls[nz1].get_side_segment(other_side);
                    let other_direction = cn_sub(other_segment[0], other_segment[1]);
                    let other_point = other_segment[0];
                    if (dummy_walls[nz1].balcony) other_point = dummy_walls[nz1].vertices[0].position;
                    let impact = cn_intersect_line(p0, d0, other_point, other_direction);
                    if (impact && cn_dot(d0, cn_sub(impact, p0)) < lc) {
                        ss.points[niter][0] = impact[0];
                        ss.points[niter][1] = impact[1];
                    }
                    if (zz1 > z1 + 0.01) {
                        nz0++;
                        continue;
                    }
                    if (z1 > zz1 + 0.01) {
                        ss.points[0][3] = ss.points[1][3] = zz1;
                        nz1++;
                        dummy_segments.splice(nz0 + 1, 0, this._dummy_space_segment(ss.wall, zz1, z1));
                    }
                    else nz1++;
                    nz0++;
                }
            }
        }

        return dummy_segments;
    }

    /**
     * Builds all facing polygons
     * @param {cn_storey} storey
     * @param {boolean} apply_openings
     * @param {function} facing_filter - If defined, a boolean function that returns true is facing is concerned.
     * @returns
     */
    _build_facing_polygons(storey, apply_openings = true, facing_filter = null) {
        let polygons = this._build_facing_polygons_3d(storey, apply_openings, facing_filter);
        if (this.upper_facings.length == 0) return polygons;
        let cut_polygons = [];
        polygons.forEach(polygon => {
            if (polygon["facing_trimming"]) {
                cut_polygons.push(polygon);
                return;
            }
            let box = polygon.get_bounding_box();
            for (var i = 0; i < this.upper_facings.length; i++) {
                if (this.upper_heights[i] >= box.position[2] + box.size[2]) {
                    cut_polygons.push(polygon);
                    return;
                }
                if (this.upper_heights[i] <= box.position[2]) {
                    polygon["facing"] = this.upper_facings[i];
                    return;
                }
                let normal = polygon.get_normal();
                let dx = cnx_cross([0, 0, 1], normal);
                cnx_normalize(dx);
                let dy = cnx_cross(dx, normal);
                cnx_normalize(dy);
                if (dy[2] < 0) dy = cnx_mul(dy, -1);
                let point = polygon.get_point();
                let box2d = new cn_box();
                polygon.contour_vertices.forEach(v => {
                    let vv = cnx_sub(v, point);
                    box2d.enlarge_point([cnx_dot(dx, vv), cnx_dot(dy, vv)]);
                });
                box2d.size[1] = (this.upper_heights[i] - point[2]) / dy[2] - box2d.posmin[1];
                box2d.enlarge_distance(0.1);
                let inter_poly = new fh_polygon(point, normal);
                let contour = [];
                contour.push(cnx_add(point, cnx_add(cnx_mul(dx, box2d.posmin[0]), cnx_mul(dy, box2d.posmin[1]))));
                contour.push(cnx_add(point, cnx_add(cnx_mul(dx, box2d.posmin[0] + box2d.size[0]), cnx_mul(dy, box2d.posmin[1]))));
                contour.push(cnx_add(point, cnx_add(cnx_mul(dx, box2d.posmin[0] + box2d.size[0]), cnx_mul(dy, box2d.posmin[1] + box2d.size[1]))));
                contour.push(cnx_add(point, cnx_add(cnx_mul(dx, box2d.posmin[0]), cnx_mul(dy, box2d.posmin[1] + box2d.size[1]))));
                inter_poly.add_contour(contour);
                let inter_poly2 = inter_poly.clone();
                inter_poly.intersects(polygon);
                if (inter_poly.get_area() > 0.001) {
                    inter_poly["facing"] = polygon["facing"];
                    cut_polygons.push(inter_poly);
                }
                polygon.substracts(inter_poly2);
                if (polygon.get_area() < 0.001) return;
                polygon["facing"] = this.upper_facings[i];
                box = polygon.get_bounding_box();
            }
            cut_polygons.push(polygon);
        });
        cut_polygons.forEach((pg, i) => {
            pg['bbp_id'] = this.create_id_for_bbp(storey, i);
        });
        return cut_polygons;
    }

    /**
     * Builds all facing polygons
     * @param {cn_storey} storey
     * @param {boolean} apply_openings
     * @param {function} facing_filter - If defined, a boolean function that returns true is facing is concerned.
     * @returns
     */
    _build_facing_polygons_3d(storey, apply_openings = true, facing_filter = null) {
        var polygons = [];
        this.facing_topo = false;
        if (!this.wall || !this.wall.has_facings()) return polygons;

        const facing_volume = (this.wall.dormer) ? this.wall.dormer.get_clipping_volume(this.wall_side) : storey.build_facing_volume(!this.is_facade(storey));

        var parallel_polygons = [];

        const topography = this.space.scene.building.topography;

        const trimmings = this._get_trimmings();

        //*** Is the native facing of this face concerned ?  */
        const this_facing_filter = (!facing_filter || facing_filter(this.facing));

        //*** If no facing is concerned, end */
        if (facing_filter &&
            !this_facing_filter &&
            !trimmings.some(trimming => facing_filter(trimming.facing)))
            return [];

        let dummy_segments = this._get_dummy_segments();
        let verticality = dummy_segments.length > 1;
        dummy_segments.forEach(ss => {
            //*** Compute geometry */
            var space_lines = [];
            let zmin = 0;
            let zmax = 0;
            if (ss.wall.wall_type.constructor == cn_wall_type) {
                const storey_bb = facing_volume.get_bounding_box();
                zmin = (this.space.outside) ? storey_bb.position[2] : this.space.slab_offset;
                zmax = storey_bb.position[2] + storey_bb.size[2];
                if (verticality) {
                    if (ss.points[0][3] <= zmin) return;
                    if (ss.points[0][2] <= zmin) {
                        ss.points[0][2] = ss.points[1][2] = zmin;
                    }
                }
                if (this.space.ceiling_type && zmax > this.space.ceiling_height + this.space.slab_offset)
                    zmax = this.space.ceiling_height + this.space.slab_offset;
                space_lines = ss.points.concat([]);
                //*** The facing polygon must ignore balconies. */
                if (!verticality && this.wall_side < 2 && ss.wall.supported_vertices.length) {
                    const vertex_balconies = ss.wall.supported_vertices.filter(v => v.support_wall_side == this.wall_side && v.walls.length == 1 && v.walls[0].balcony);
                    vertex_balconies.forEach(vb => {
                        const support_segment = vb.get_support_segment();
                        if (!support_segment) return;
                        if (cn_dist(support_segment[0], space_lines[1]) < 0.01)
                            space_lines[1] = support_segment[1];
                        else if (cn_dist(support_segment[1], space_lines[0]) < 0.01) {
                            //*** We add the balcony before only if no other segment can take it after. */
                            if (!ss.wall.space_segments.filter(ss => ss.wall_side == this.wall_side).find(ss => (cn_dist(support_segment[0], ss.points[1]) < 0.001)))
                                space_lines[0] = support_segment[0];
                        }
                    });
                }
            }
            else if (ss.wall.wall_type.constructor == cn_balcony_type) {
                const storey_bb = facing_volume.get_bounding_box();
                zmin = (this.space.outside) ? storey_bb.position[2] : this.space.slab_offset;
                zmax = zmin + ss.wall.wall_type.height;
                let wall_interfaces = ss.wall.wall_interfaces.filter(wi => wi.space_segments.includes(this));
                wall_interfaces.forEach(wi => {
                    let z = wi.get_highest_slab_height() + ss.wall.wall_type.height;
                    if (z > zmax) zmax = z;
                    let wiface = wi.space_segments.indexOf(this);
                    let pw0 = (wiface == 0) ? cnx_clone(wi.shape[0]) : cnx_clone(wi.shape[2]);
                    let pw1 = (wiface == 0) ? cnx_clone(wi.shape[3]) : cnx_clone(wi.shape[1]);
                    pw1[2] = zmin;
                    pw1.push(z);
                    pw0[2] = zmin;
                    pw0.push(z);
                    space_lines.push(pw0);
                    space_lines.push(pw1);
                });
                //space_lines = this.points;
            }
            else if (ss.wall.wall_type.constructor == cn_fence_type) {
                space_lines = ss.wall.wall_type.compute_space_line_slices(this, (storey.exterior) ? storey.building.topography : null);
            }

            for (var i = 0; i < space_lines.length - 1; i += 2) {
                var p0 = cnx_clone(space_lines[i]);
                var p1 = cnx_clone(space_lines[i + 1]);
                var nor = cn_normal(cn_sub(p0, p1));
                if (cn_size(nor) < 0.001) continue;
                nor.push(0);
                cnx_normalize(nor);

                var polygon = new fh_polygon(cnx_add(p0, cnx_mul(nor, -0.001)), nor);
                if (this_facing_filter)
                    polygons.push(polygon);

                if (verticality || ss.wall.wall_type.constructor == cn_balcony_type) {
                    zmin = space_lines[i][2];
                    zmax = space_lines[i][3];
                }
                else if (ss.wall.wall_type.constructor == cn_fence_type) {
                    zmin = space_lines[i][2];
                    zmax = zmin + this.wall.wall_type.wall_height;
                }

                let contour = [];
                p0[2] = zmin;
                contour.push(cnx_clone(p0));
                p0[2] = zmax;
                contour.push(cnx_clone(p0));
                p1[2] = zmax;
                contour.push(cnx_clone(p1));
                p1[2] = zmin;
                contour.push(cnx_clone(p1));
                polygon.add_contour(contour);

                //*** maybe add topography ? */
                if (!ss.wall.dormer && this.space.outside && this.space.scene.building.facings_above_ground && zmin + storey.altitude < topography.max_height + topography.z + this.space.scene.building.facings_above_ground_height) {
                    const topo_sampling = 0.5;
                    contour = [];
                    const fdir = cn_sub(p1, p0);
                    const flength = cn_normalize(fdir);
                    let has_topo = false;
                    for (var x = -topo_sampling; x < flength + topo_sampling; x += topo_sampling) {
                        const fp = cnx_add(p0, cn_mul(fdir, x));
                        fp[2] = topography.compute_height(fp) + this.space.scene.building.facings_above_ground_height - storey.altitude;
                        has_topo ||= fp[2] > zmin;
                        contour.push(fp);
                    }
                    if (has_topo) {
                        contour.push(cnx_clone(contour[contour.length - 1], zmin - 0.1));
                        contour.push(cnx_clone(contour[0], zmin - 0.1));
                        var topo_polygon = new fh_polygon(cnx_add(p0, cnx_mul(nor, -0.001)), nor);
                        topo_polygon.add_contour(contour);
                        polygon.substracts(topo_polygon);
                        this.facing_topo = true;
                    }
                }

                //*** Add facing trimmings */
                if (Math.abs(cn_dot(nor, ss.wall.bounds.direction)) < 0.01) {
                    if (this_facing_filter)
                        parallel_polygons.push(polygon);

                    if (trimmings.length) {
                        const face_polygon = polygon.clone();
                        const previous_polygons = [polygon];
                        trimmings.forEach(ft => {
                            const pg = new fh_polygon();
                            const shape = ft.get_shape_3d(null);
                            shape.forEach(v => v[2] -= storey.altitude);
                            pg.add_contour(shape);
                            pg.compute_contours();
                            pg.intersects(face_polygon);
                            if (pg.get_area() > 0.01) {
                                previous_polygons.forEach(pp => pp.substracts(pg));
                                pg['facing_trimming'] = ft;
                                previous_polygons.push(pg);
                                if (!facing_filter || facing_filter(ft.facing)) {
                                    parallel_polygons.push(pg);
                                    polygons.push(pg);
                                }
                            }
                        });
                    }
                }
            }
        });

        //*** Apply openings */
        if (apply_openings) {
            parallel_polygons.forEach(pg => this.wall.remove_openings_from_polygon(pg, storey));
            parallel_polygons.forEach(pg => this.wall.remove_niches_from_polygon(pg, this.wall_side));
        }

        polygons.forEach((facing_polygon, index) => {
            //*** intersect with roof volume */
            if (facing_volume) facing_volume.intersects_polygon(facing_polygon);
            facing_polygon['bbp_id'] = this.create_id_for_bbp(storey, index);
            //*** small translation of the facing to avoid zfight with wall */
            cn_translate_polygon(facing_polygon, cnx_mul(facing_polygon.get_normal(), 0.002));
        });

        //*** merge parallel polygons */
        while (true) {
            let change = false;
            for (var n0 = 0; n0 < polygons.length; n0++) {
                let pg0 = polygons[n0];
                let normal0 = pg0.get_normal();
                let point0 = pg0.get_point();
                for (var n1 = n0 + 1; n1 < polygons.length; n1++) {
                    let pg1 = polygons[n1];
                    if (pg0['facing_trimming'] != pg1['facing_trimming']) continue;
                    if (cnx_manhattan_distance(normal0, pg1.get_normal()) > 0.001) continue;
                    if (Math.abs(cnx_dot(normal0, cnx_sub(pg1.get_point(), point0))) > 0.001) continue;
                    pg0.unites(pg1);
                    polygons.splice(n1, 1);
                    change = true;
                    break;
                }
                if (change) break;
            }
            if (!change) break;
        }
        polygons.forEach(pg => {
            if (pg['facing_trimming'])
                pg["facing"] = pg['facing_trimming'].facing;
            else
                pg["facing"] = this.facing;
        });
        return polygons;
    }

    /**
     * Updates facings geometries
     * @param {cn_3d_building} building_3d
     * @param {cn_storey} storey
     */
    update_3d_facings(building_3d, storey) {
        var res = 0;
        if (!this.wall || !this.wall.has_facings()) return res;

        const filter = ob => {
            if (ob.cnmap_storey != storey) return false;
            if (ob.json_object.cnmap_space_segment && ob.json_object.cnmap_space_segment == this) return true;
            return false;
        };
        let facing_objects = building_3d.get_3d_objects_filter(filter);

        //*** check that anything has changed */
        var need_build = true;
        while (true) {
            if (!this.up_to_date(building_3d._update_date, 'upper_heights')) break;
            if (this.upper_facings.length) break;

            //*** Change in topography */
            if ((storey.exterior || !this.space.has_roof) &&
                (this.facing_topo || this._facing_has_topo(storey)) &&
                (!building_3d._building.topography.up_to_date(building_3d._update_date, 'heights') ||
                    !building_3d._building.up_to_date(building_3d._update_date, 'facings_above_ground') ||
                    !building_3d._building.up_to_date(building_3d._update_date, 'facings_above_ground_height'))) {
                break;
            }
            //*** Change if some trimmings have changed */
            if (this._get_trimmings().some(wt => !wt.up_to_date_shape(building_3d._update_date))) {
                break;
            }

            var old_trimmings = [];
            facing_objects.forEach(ob => {
                if (ob.json_object && ob.json_object.cnmap_trimmings) old_trimmings = ob.json_object.cnmap_trimmings;
            });

            //*** change if an old trimming no longer exists */
            if (old_trimmings.some(wt => this.space.scene.building.facing_trimmings.indexOf(wt) < 0)) {
                break;
            }

            //*** change if an old trimming is not up to date */
            if (old_trimmings.some(wt => !wt.up_to_date_shape(building_3d._update_date))) {
                break;
            }
            need_build = false;
            break;
        }
        if (!need_build) return 0;

        //*** There were changes */
        building_3d.remove_objects(facing_objects);
        building_3d.add_bbp_objects(this.build_bbp_facings(storey, true));
        res++;
        return res;
    }

    /**
     * Returns true if this facing may be concerned by topography
     * @param {cn_storey} storey
     * @returns {boolean}
     */
    _facing_has_topo(storey) {
        if (!this.wall || !this.wall.has_facings()) return false;
        if (storey.exterior) return true;
        if (this.wall.dormer) return false;
        if (!this.space.outside) return false;
        if (!this.space.scene.building.facings_above_ground) return false;
        const topography = this.space.scene.building.topography;
        const zmin = storey.altitude + storey.build_facing_volume().get_bounding_box().position[2];
        return zmin < topography.max_height + topography.z + this.space.scene.building.facings_above_ground_height;
    }

    /**
     * Returns the list of trimmings that may affect this facing.
     * @returns {cn_facing_trimming[]}
     */
    _get_trimmings() {
        if (!this.wall || !this.wall.has_facings()) return [];
        const wall_point = this.points[0];
        var wall_normal = cn_mul(this.normal, -1);

        function trimming_filter(f) {
            if (f.wall == null) return false;
            if (cn_dot(f.wall_normal, wall_normal) < 0.999) return false;
            if (Math.abs(cn_dot(wall_normal, cn_sub(f.wall_point, wall_point))) > 0.05) return false;
            return true;
        };
        return this.space.scene.building.facing_trimmings.filter(ft => trimming_filter(ft));

    }


    /**
     * Builds bbp objects that match moldings
     * @param {cn_storey} storey
     * @param {boolean} cnmap_pointers
     * @param {BbpHelper} bbpHelper
     * @returns
     */
    build_bbp_moldings(storey, cnmap_pointers = false, bbpHelper = null) {
        if (!this.wall || !this.wall.has_facings()) return [];

        let bbp_objects = [];
        this.moldings.forEach((molding, i) => {
            let solid = this.build_molding_solid(i, storey);
            if (!solid) return;

            //*** build facing */
            var bbp_molding = {};
            bbp_objects.push(bbp_molding);
            bbp_molding.ID = cn_building.create_unique_id(this, storey, i);
            if (this.is_outdoor()) {
                bbp_molding.Name = 'Plinthe';
                bbp_molding.Code_BIM = CODES_BIM_OUVRAGES.facing_facade;
            } else {
                bbp_molding.Name = 'Moulure';
                bbp_molding.Code_BIM = CODES_BIM_OUVRAGES.facing_wall;
                if (this.space) bbp_molding.SPACE = this.space.create_id_for_bbp(storey);
            }
            bbp_molding.storey = (this.wall && this.wall.dormer) ? storey.storey_index + 1 : storey.storey_index;
            if (cnmap_pointers) {
                bbp_molding.cnmap_storey = storey;
                bbp_molding.cnmap_element = this;
            }

            const geometry = cn_bbp_geometry.from_solid(solid, storey.altitude);
            const facing = this.molding_facings[i];
            if (facing) {
                geometry.texture = cn_image_dir() + 'texture_' + facing.texture + '.jpg';
                geometry.color = [...cn_color_hexa_to_rgb(facing.color || '#FFFFFF'), 1];
            }

            bbp_molding.geometries = [geometry];
        });

        return bbp_objects;
    }

    /**
     * Lazy Builds the molding volume
     * @param {number} molding_index
     * @param {cn_storey} storey
     * @returns {fh_solid}
     */
    build_molding_solid(molding_index, storey) {
        // @ts-ignore
        return this.get_lazy_data(() => {
            if (molding_index >= this.moldings.length) return null;
            return this._build_molding_solid(this.moldings[molding_index], storey);
        }, "build_molding_solid_" + molding_index, storey)
    }

    /**
     * Builds the molding volume
     * @param {cn_molding_type} molding_type
     * @param {cn_storey} storey
     * @returns {fh_solid}
     */
    _build_molding_solid(molding_type, storey) {
        let z0 = this.space.slab_offset + molding_type.z;
        let z1 = z0 + molding_type.height;
        let p0 = cnx_clone(this.points[0], z0);
        let normal = cnx_mul(this.normal, -1);
        let base = new fh_polygon(p0, normal);
        base.add_contour([p0, cnx_clone(this.points[1], z0), cnx_clone(this.points[1], z1), cnx_clone(this.points[0], z1)]);
        this.wall.remove_openings_from_polygon(base, storey);
        let solid = new fh_solid();
        solid.extrusion(base, cnx_mul(normal, molding_type.thickness));
        solid.intersects(storey.build_space_volume());

        let segments = [];

        solid.get_faces().forEach(face => {
            let xmin = this.size;
            let xmax = 0;
            face.contour_vertices.forEach(v => {
                let x = cn_dot(cn_sub(v, this.points[0]), this.direction);
                if (x > xmax) xmax = x;
                if (x < xmin) xmin = x;
            });
            if (xmin < xmax) segments.push([xmin, xmax]);
        });

        while (true) {
            let change = false;
            for (var i = 0; i < segments.length; i++) {
                let xmin0 = segments[i][0];
                let xmax0 = segments[i][1];
                for (var j = i + 1; j < segments.length; j++) {
                    let xmin1 = segments[j][0];
                    let xmax1 = segments[j][1];
                    if (xmax0 < xmin1 || xmin0 > xmax1) continue;
                    segments[i][0] = Math.min(xmin0, xmin1);
                    segments[i][1] = Math.max(xmax0, xmax1);
                    segments.splice(j, 1);
                    change = true;
                    break;
                }
                if (change) break;
            }
            if (!change) break;
        }
        solid["metrics"] = 0;
        segments.forEach(s => solid["metrics"] += s[1] - s[0]);

        return solid;
    }

    /**
     * Returns the molding's metrics
     * @param {number} molding_index
     * @param {cn_storey} storey
     * @returns {number}
     */
    get_molding_metrics(molding_index, storey) {
        let solid = this.build_molding_solid(molding_index, storey);
        if (!solid) return 0;
        return solid["metrics"];
    }

    /**
     * Returns next space segment on space
     * @returns {cn_space_segment}
     */
    get_next() {
        return this.get_other(1);
    }

    /**
     * Returns previous space segment on space
     * @returns {cn_space_segment}
     */
    get_previous() {
        return this.get_other(-1);
    }

    /**
     * Returns other space segment on space
     * @param {number} offset
     * @returns {cn_space_segment}
     */
    get_other(offset) {
        const contour = this.space.space_segments.find(sslist => sslist.includes(this));
        if (!contour) return null;
        const index = contour.indexOf(this);
        return contour[(index + contour.length + offset) % contour.length];
    }

    same_facings(other) {
        if (this.facing != other.facing) return false;
        if (this.upper_facings.length != other.upper_facings.length) return false;
        for (var i = 0; i < this.upper_facings.length; i++) {
            if (this.upper_facings[i] != other.upper_facings[i]) return false;
            if (this.upper_heights[i] != other.upper_heights[i]) return false;
        }
        return true;
    }

    copy_facings(other) {
        this.facing = other.facing;
        this.substrate = other.substrate;
        this.upper_facings = other.upper_facings.concat([]);
        this.upper_heights = other.upper_heights.concat([]);
        this.upper_substrates = other.upper_substrates.concat([]);
    }

    /**
     * Returns true if the segment may have a numerotation
     * */
    can_have_numerotation() {
        if (this.size < 0.05) return false;
        if (!this.wall || this.wall.wall_type.constructor != cn_wall_type || this.wall.wall_type.free) return false;
        return true;
    }

    /**
     * Returns true if the two segments are consecutive and parallel
     * @param {cn_space_segment} ss
     * @returns {boolean}
     */
    continues(ss) {
        if (!this.can_have_numerotation() || !ss.can_have_numerotation()) return false;
        if (cn_dist(ss.points[1], this.points[0]) > 0.01) return false;
        return cn_dist(this.direction, ss.direction) < 0.01;
    }

    /**
     * returns the filtered list of ids that are used in this
     * @param {string[]} scope_ids
     * @returns {string[]}
     */
    in_scope(scope_ids) {
        return scope_ids.filter(id => {
            return [...this.moldings,
            this.facing,
            ...this.upper_facings].some(e => e && e.ID == id);
        });
    }
}
